import { type HTMLAttributes, type ReactNode } from "react";
import { useInterruptCard } from "../InterruptCardContext";
import { humanizeKey } from "../utils";

/**
 * Default read-only renderer for `action_request.args`. For domain-specific
 * UI (e.g. the per-site/page breakdown the editCustomField interrupt
 * carries), consumers usually replace this with their own children inside
 * `<InterruptCard>` rather than render the generic dump.
 *
 * Hides keys that are pure dispatch metadata (`toolName`) by default.
 *
 * @example
 *   <InterruptCard.Args />                       // default dump
 *   <InterruptCard.Args hide={["sites"]} />      // skip noisy keys
 *   <InterruptCard.Args
 *     render={(key, value) => <MyRow k={key} v={value} />}
 *   />
 */
export interface ArgsProps extends HTMLAttributes<HTMLDListElement> {
  /** Keys to omit from the rendered list. */
  hide?: string[];
  /** Custom row renderer; default stringifies values. */
  render?: (key: string, value: unknown) => ReactNode;
}

const DEFAULT_HIDDEN = new Set(["toolName"]);

export function Args({ hide, render, ...props }: ArgsProps) {
  const { interrupt } = useInterruptCard();
  const args = interrupt.action_request.args ?? {};
  const hiddenSet = new Set([...(hide ?? []), ...DEFAULT_HIDDEN]);

  const entries = Object.entries(args).filter(([k]) => !hiddenSet.has(k));
  if (entries.length === 0) return null;

  return (
    <dl
      {...props}
      className={["space-y-1 text-sm", props.className].filter(Boolean).join(" ")}
    >
      {entries.map(([key, value]) =>
        render ? (
          <div key={key}>{render(key, value)}</div>
        ) : (
          <div key={key} className="flex gap-2">
            <dt className="text-zinc-500">{humanizeKey(key)}</dt>
            <dd className="text-zinc-900 font-mono break-all">{stringify(value)}</dd>
          </div>
        )
      )}
    </dl>
  );
}

function stringify(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}
